import React, { useState, useEffect } from 'react';
import { post } from '../api';
import { CheckIcon } from '@heroicons/react/24/solid';
import {
  BoltIcon,
  CubeIcon,
  InformationCircleIcon,
  XMarkIcon,
  QueueListIcon,
  ChevronRightIcon
} from '@heroicons/react/24/outline';
import Tooltip from './Tooltip';
import FAQ from './FAQ';
import '@google/model-viewer';

const PAYWALL_EXAMPLES = [
  {
    id: 'rpg-prop',
    prompt: "A stylized fantasy treasure chest, low poly, hand-painted wooden texture with iron bands, glowing gold coins inside, World of Warcraft art style.",
    image: "https://files.compress-glb.com/examples/the-rpg-prop/Front_view.png",
    model_url: "https://files.compress-glb.com/examples/the-rpg-prop/the-rpg-prop.glb"
  },
  {
    id: 'mobile-char',
    prompt: "A cute blocky robot helper, voxel art style, orange and white color scheme, simple geometry, floating antennas, vibrant colors.",
    image: "https://files.compress-glb.com/examples/the-mobile-game-character/Front_view.png",
    model_url: "https://files.compress-glb.com/examples/the-mobile-game-character/the-mobile-game-character.glb"
  },
  {
    id: 'env-asset',
    prompt: "A collection of stylized pine trees, low polygon count, gradient green textures, snowy tips, suitable for a mobile strategy game map.",
    image: "https://files.compress-glb.com/examples/the-environment-asset/Front_view.png",
    model_url: "https://files.compress-glb.com/examples/the-environment-asset/the-environment-asset.glb"
  },
  {
    id: 'furniture',
    prompt: "Mid-century modern armchair, mustard yellow velvet upholstery, dark walnut wood tapered legs, studio lighting, photorealistic 2k textures.",
    image: "https://files.compress-glb.com/examples/the-furniture-concept/Front_view.png",
    model_url: "https://files.compress-glb.com/examples/the-furniture-concept/the-furniture-concept.glb"
  },
  {
    id: 'fashion',
    prompt: "Futuristic high-top sneaker, chunky sole, holographic white leather material, neon blue accents, streetwear style, clean commercial render.",
    image: "https://files.compress-glb.com/examples/the-fashion-item/Front_view.png",
    model_url: "https://files.compress-glb.com/examples/the-fashion-item/the-fashion-item.glb"
  },
  {
    id: 'electronics',
    prompt: "Retro-futuristic portable radio, matte black metal finish with brass knobs, orange digital display, industrial design aesthetic.",
    image: "https://files.compress-glb.com/examples/the-consumer-electronics/Front_view.png",
    model_url: "https://files.compress-glb.com/examples/the-consumer-electronics/the-consumer-electronics.glb"
  },
  {
    id: 'toon',
    prompt: "A vintage 1950s convertible car, bright candy red, exaggerated proportions, cartoon outlines, cel-shaded texture, Anime style.",
    image: "https://files.compress-glb.com/examples/the-toon-shader/Front_view.png",
    model_url: "https://files.compress-glb.com/examples/the-toon-shader/the-toon-shader.glb"
  },
  {
    id: 'material',
    prompt: "An abstract knot shape made of liquid mercury, highly reflective metallic surface, smooth curves, hdri studio environment reflection.",
    image: "https://files.compress-glb.com/examples/the-material-study/Front_view.png",
    model_url: "https://files.compress-glb.com/examples/the-material-study/the-material-study.glb"
  },
  {
    id: 'minimalist',
    prompt: "Origami paper cat, sharp geometric folds, white textured paper material, soft ambient occlusion shadows, minimal design.",
    image: "https://files.compress-glb.com/examples/the-minimalist-decor/Front_view.png",
    model_url: "https://files.compress-glb.com/examples/the-minimalist-decor/the-minimalist-decor.glb"
  },
  {
    id: 'cyberpunk',
    prompt: "Cyberpunk street food kiosk, Japanese neon signs, metallic textures with rust and grime, steam vents, night time lighting atmosphere.",
    image: "https://files.compress-glb.com/examples/the-cyberpunk-element/Front_view.png",
    model_url: "https://files.compress-glb.com/examples/the-cyberpunk-element/the-cyberpunk-element.glb"
  },
  {
    id: 'scifi',
    prompt: "Worn astronaut helmet, gold reflective visor, white tactical padding, scratches and battle damage, realistic sci-fi prop, Unreal Engine 5 style.",
    image: "https://files.compress-glb.com/examples/the-sci-fi-prop/Front_view.png",
    model_url: "https://files.compress-glb.com/examples/the-sci-fi-prop/the-sci-fi-prop.glb"
  },
  {
    id: 'isometric',
    prompt: "Isometric magic potion shop, purple roof tiles, smoke coming from chimney, cozy atmosphere, 3D icon style, floating on a small island of dirt.",
    image: "https://files.compress-glb.com/examples/the-isometric-building/Front_view.png",
    model_url: "https://files.compress-glb.com/examples/the-isometric-building/the-isometric-building.glb"
  }
];

function ModelPreview({ model }) {
  const [show3D, setShow3D] = useState(false);

  return (
    <div className="group relative aspect-square rounded-xl overflow-hidden bg-background-soft border border-border hover:border-indigo-500/50 transition-all">
      {show3D ? (
        <div className="w-full h-full relative bg-[radial-gradient(circle_at_center,_var(--tw-gradient-stops))] from-gray-800 via-gray-950 to-black">
          <model-viewer
            src={model.model_url}
            poster={model.image}
            alt={model.prompt}
            auto-rotate
            camera-controls
            shadow-intensity="1"
            shadow-softness="1"
            environment-image="neutral"
            exposure="1.0"
            style={{ width: '100%', height: '100%' }}
          >
            <div slot="poster" className="flex items-center justify-center w-full h-full bg-background-soft">
              <div className="w-16 h-16 border-2 border-foreground/20 border-t-foreground rounded-full animate-spin"></div>
            </div>
          </model-viewer>
          <button
            onClick={(e) => {
              e.stopPropagation();
              setShow3D(false);
            }}
            className="absolute top-2 right-2 p-1 bg-black/50 hover:bg-black/70 text-white rounded-full backdrop-blur-sm transition-colors z-10"
            title="Close 3D view"
          >
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>
      ) : (
        <>
          <img
            src={`${model.image}?t=${Date.now()}`}
            alt={model.prompt}
            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex flex-col justify-end p-4">
            <p className="text-white text-sm font-medium line-clamp-2 mb-3">"{model.prompt}"</p>
            <button
              onClick={(e) => {
                e.stopPropagation();
                setShow3D(true);
              }}
              className="w-full py-2 bg-white/20 hover:bg-white/30 backdrop-blur-sm text-white text-xs font-bold rounded-lg transition-colors flex items-center justify-center gap-2"
            >
              <CubeIcon className="w-4 h-4" />
              View 3D model
            </button>
          </div>
        </>
      )}
    </div>
  );
}

const PLANS = [
  {
    id: 'starter',
    name: 'Starter',
    description: 'Perfect for hobbyists and experimentation',
    credits: 500,
    priority: 'Standard queue priority',
    monthly: {
      price: 19,
      lookupKey: 'starter_monthly',
    },
    annual: {
      price: 15,
      lookupKey: 'starter_annual',
      billAmount: 180
    }
  },
  {
    id: 'pro',
    name: 'Pro',
    description: 'For creators and indie developers',
    credits: 1500,
    priority: 'High queue priority',
    popular: true,
    monthly: {
      price: 49,
      lookupKey: 'pro_monthly',
    },
    annual: {
      price: 39,
      lookupKey: 'pro_annual',
      billAmount: 468
    }
  },
  {
    id: 'business',
    name: 'Business',
    description: 'For studios and high-volume needs',
    credits: 5000,
    priority: 'Highest queue priority',
    monthly: {
      price: 99,
      lookupKey: 'business_monthly',
    },
    annual: {
      price: 79,
      lookupKey: 'business_annual',
      billAmount: 948
    }
  }
];

export default function AiStudioPaywall() {
  const [billingInterval, setBillingInterval] = useState('annual');
  const [loading, setLoading] = useState(false);
  const [coupon, setCoupon] = useState(null);

  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    const couponParam = searchParams.get('coupon');
    if (couponParam) {
      setCoupon(couponParam);
    }
  }, []);

  const handleSubscribe = async (priceLookupKey) => {
    setLoading(true);
    try {
      const response = await post('/api/1/ai-studio-billing/create-checkout-session', {
        price_lookup_key: priceLookupKey,
        coupon_code: coupon
      });
      if (response.checkout_url) {
        window.location.href = response.checkout_url;
      }
    } catch (error) {
      console.error('Checkout error:', error);
      alert('Failed to start checkout. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background text-foreground pb-20 font-sans">
      {/* Discount Banner */}
      {coupon === 'AISTUDIO30' && (
        <div className="sticky top-0 z-50 bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 text-white py-3 px-4 text-center font-black text-sm uppercase tracking-widest shadow-lg animate-in slide-in-from-top duration-500">
          🎉 30% OFF APPLIED! LIMITED TIME OFFER 🎉
        </div>
      )}

      {/* Hero Section */}
      <div className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-purple-500/5 to-background pointer-events-none" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-48 pb-20 text-center relative z-10">
          <h1 className="text-5xl md:text-7xl font-black tracking-tighter mb-6 uppercase leading-tight">
            Generate <span className="text-transparent bg-clip-text bg-gradient-to-r from-indigo-400 via-purple-400 to-pink-400">Production-Ready</span><br />
            3D Assets Instantly
          </h1>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto mb-10 leading-relaxed">
            Stop hunting for assets. Start creating them. <br />
            The AI-powered studio for game developers, designers, and rapid prototypers.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-12">
            <button
              onClick={() => document.getElementById('pricing').scrollIntoView({ behavior: 'smooth' })}
              className="group px-10 py-4 rounded-full bg-foreground text-background hover:bg-foreground/90 font-bold text-sm uppercase tracking-widest shadow-xl shadow-purple-500/20 transition-all hover:scale-105 flex items-center gap-2"
            >
              Start Creating
              <ChevronRightIcon className="w-4 h-4 transition-transform duration-300 group-hover:rotate-90" />
            </button>
          </div>

          <p className="text-xs font-mono text-muted-foreground uppercase tracking-widest">
            5,500+ Happy Users <span className="mx-2">•</span> 50,000+ Models Processed <span className="mx-2">•</span> 99.9% Uptime <span className="mx-2">•</span> 99.9% Success Rate
          </p>
        </div>
      </div>

      {/* Sample Models Grid */}
      <div className="max-w-[1600px] mx-auto px-6 py-16">
        <div className="text-center mb-16">
          <h2 className="text-2xl font-bold uppercase tracking-tight mb-4">Community Creations</h2>
          <p className="text-xs font-mono text-muted-foreground uppercase tracking-widest">Recent Generations</p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-12">
          {PAYWALL_EXAMPLES.map((model) => (
            <ModelPreview key={model.id} model={model} />
          ))}
        </div>

        <div className="flex justify-center mt-12">
          <button
            onClick={() => document.getElementById('pricing').scrollIntoView({ behavior: 'smooth' })}
            className="group px-10 py-4 rounded-full bg-foreground text-background hover:bg-foreground/90 font-bold text-sm uppercase tracking-widest shadow-xl shadow-purple-500/20 transition-all hover:scale-105 flex items-center gap-2"
          >
            Create Your Own Asset
            <ChevronRightIcon className="w-4 h-4 transition-transform duration-300 group-hover:rotate-90" />
          </button>
        </div>
      </div>

      {/* How It Works - NEW */}
      <div className="py-24 border-y border-border/50 bg-background/30">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-black uppercase tracking-tighter mb-4">From Prompt to Prop</h2>
            <p className="text-sm font-mono text-muted-foreground uppercase tracking-widest">Three simple steps to your next asset</p>
          </div>
          <div className="grid md:grid-cols-3 gap-12 relative">
            {/* Connecting Line (Desktop) */}
            <div className="hidden md:block absolute top-12 left-[16%] right-[16%] h-0.5 bg-gradient-to-r from-transparent via-purple-500/30 to-transparent" />

            <div className="relative text-center group">
              <div className="w-24 h-24 mx-auto bg-background border border-border rounded-2xl flex items-center justify-center mb-6 relative z-10 group-hover:border-purple-500/50 transition-colors shadow-lg">
                <img src="/images/talk.png" alt="Describe" className="w-16 h-16 object-contain" />
              </div>
              <h3 className="text-lg font-bold uppercase tracking-wide mb-2">1. Describe</h3>
              <p className="text-sm text-muted-foreground">Enter a text prompt or upload a reference image to define your vision.</p>
            </div>
            <div className="relative text-center group">
              <div className="w-24 h-24 mx-auto bg-background border border-border rounded-2xl flex items-center justify-center mb-6 relative z-10 group-hover:border-purple-500/50 transition-colors shadow-lg">
                <img src="/images/magic.png" alt="Generate" className="w-16 h-16 object-contain" />
              </div>
              <h3 className="text-lg font-bold uppercase tracking-wide mb-2">2. Generate</h3>
              <p className="text-sm text-muted-foreground">Our AI constructs geometry and generates 2k textures in under a minute.</p>
            </div>
            <div className="relative text-center group">
              <div className="w-24 h-24 mx-auto bg-background border border-border rounded-2xl flex items-center justify-center mb-6 relative z-10 group-hover:border-purple-500/50 transition-colors shadow-lg">
                <img src="/images/download.png" alt="Export" className="w-16 h-16 object-contain" />
              </div>
              <h3 className="text-lg font-bold uppercase tracking-wide mb-2">3. Export</h3>
              <p className="text-sm text-muted-foreground">Download game-ready GLB files compatible with Unity, Unreal, and Blender.</p>
            </div>
          </div>

          <div className="flex justify-center mt-16">
            <button
              onClick={() => document.getElementById('pricing').scrollIntoView({ behavior: 'smooth' })}
              className="group px-10 py-4 rounded-full bg-foreground text-background hover:bg-foreground/90 font-bold text-sm uppercase tracking-widest shadow-xl shadow-purple-500/20 transition-all hover:scale-105 flex items-center gap-2"
            >
              Start Generating Now
              <ChevronRightIcon className="w-4 h-4 transition-transform duration-300 group-hover:rotate-90" />
            </button>
          </div>
        </div>
      </div>

      {/* Features */}
      <div className="bg-background/30 backdrop-blur-sm py-24 border-b border-border/50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-black uppercase tracking-tighter mb-4">Why Choose Our Studio</h2>
            <p className="text-sm font-mono text-muted-foreground uppercase tracking-widest">Built for professional workflows</p>
          </div>
          <div className="grid md:grid-cols-3 gap-12">
            <div className="p-8 text-center rounded-3xl bg-background/50 border border-border/50 hover:border-purple-500/30 transition-colors group">
              <div className="flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                <img src="/images/assembly-line.png" alt="Production Ready" className="w-20 h-20 object-contain" />
              </div>
              <h3 className="text-lg font-bold uppercase tracking-wide mb-3">Production Ready</h3>
              <p className="text-xs font-mono text-muted-foreground leading-relaxed">High-fidelity topology and textures optimized for real-time rendering engines.</p>
            </div>
            <div className="p-8 text-center rounded-3xl bg-background/50 border border-border/50 hover:border-purple-500/30 transition-colors group">
              <div className="flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                <img src="/images/papers-stack.png" alt="Full Ownership" className="w-20 h-20 object-contain" />
              </div>
              <h3 className="text-lg font-bold uppercase tracking-wide mb-3">Full Ownership</h3>
              <p className="text-xs font-mono text-muted-foreground leading-relaxed">100% commercial rights transfer. Your assets, your intellectual property.</p>
            </div>
            <div className="p-8 text-center rounded-3xl bg-background/50 border border-border/50 hover:border-purple-500/30 transition-colors group">
              <div className="flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                <img src="/images/quickness.png" alt="Rapid Generation" className="w-20 h-20 object-contain" />
              </div>
              <h3 className="text-lg font-bold uppercase tracking-wide mb-3">Rapid Generation</h3>
              <p className="text-xs font-mono text-muted-foreground leading-relaxed">Accelerated generation pipeline. Iterate at the speed of thought.</p>
            </div>
          </div>
        </div>
      </div>

      {/* Pricing Section */}
      <div id="pricing" className="max-w-7xl mx-auto px-6 py-24">
        <div className="text-center mb-16">
          <h2 className="text-3xl font-black uppercase tracking-tighter mb-8">Select Plan</h2>

          <div className="inline-flex bg-background/50 backdrop-blur-md p-2 rounded-full border border-border/50">
            <button
              onClick={() => setBillingInterval('monthly')}
              className={`px-6 py-2 rounded-full text-xs font-bold uppercase tracking-wider transition-all ${billingInterval === 'monthly'
                  ? 'bg-foreground text-background shadow-lg'
                  : 'text-muted-foreground hover:text-foreground'
                }`}
            >
              Monthly
            </button>
            <button
              onClick={() => setBillingInterval('annual')}
              className={`px-6 py-2 rounded-full text-xs font-bold uppercase tracking-wider transition-all ${billingInterval === 'annual'
                  ? 'bg-foreground text-background shadow-lg'
                  : 'text-muted-foreground hover:text-foreground'
                }`}
            >
              Yearly <span className="text-green-500 ml-1">(2 months free)</span>
            </button>
          </div>
        </div>

        <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto items-start">
          {PLANS.map((plan) => {
            const price = billingInterval === 'annual' ? plan.annual.price : plan.monthly.price;
            const lookupKey = billingInterval === 'annual' ? plan.annual.lookupKey : plan.monthly.lookupKey;

            // Calculate discounted price if coupon is AISTUDIO30
            const isDiscounted = coupon === 'AISTUDIO30';
            const displayPrice = isDiscounted ? Math.round(price * 0.7) : price;

            return (
              <div
                key={plan.id}
                className={`relative rounded-3xl p-8 flex flex-col transition-all duration-500 ${plan.popular
                    ? 'bg-background/80 backdrop-blur-xl border-2 border-purple-500/50 shadow-2xl shadow-purple-500/20 scale-105 z-10'
                    : 'border border-border/50 bg-background/30 hover:bg-background/50'
                  }`}
              >
                {plan.popular && (
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-purple-600 text-white text-[10px] font-bold px-4 py-1 rounded-full shadow-lg uppercase tracking-widest">
                    Recommended
                  </div>
                )}

                <div className="mb-8 text-center">
                  <h3 className="text-lg font-black uppercase tracking-widest mb-2">{plan.name}</h3>
                  <p className="text-xs font-mono text-muted-foreground h-8">{plan.description}</p>
                </div>

                <div className="mb-8 text-center">
                  <div className="flex items-baseline justify-center gap-1">
                    {isDiscounted && (
                      <span className="text-2xl text-muted-foreground line-through mr-2 decoration-red-500/50 decoration-2">${price}</span>
                    )}
                    <span className={`text-5xl font-light ${isDiscounted ? 'text-green-500 font-bold' : ''}`}>${displayPrice}</span>
                    <span className="text-muted-foreground text-sm font-mono">/mo</span>
                  </div>

                  {isDiscounted && (
                    <p className="text-[10px] font-bold text-green-500 mt-1 uppercase tracking-wide">
                      For the first {billingInterval === 'annual' ? 'year' : 'month'}
                    </p>
                  )}

                  {billingInterval === 'annual' && (
                    <p className="text-[10px] font-mono text-muted-foreground mt-2 uppercase">
                      Billed ${isDiscounted ? Math.round(plan.annual.billAmount * 0.7) : plan.annual.billAmount} yearly
                    </p>
                  )}
                </div>

                <div className="space-y-4 mb-8 flex-grow">
                  <div className="flex items-center gap-3">
                    <div className="p-1 rounded-full bg-purple-500/10 text-purple-500">
                      <BoltIcon className="w-4 h-4" />
                    </div>
                    <span className="font-mono text-xs uppercase tracking-wide font-bold">{plan.credits} credits</span>
                    <Tooltip
                      show={true}
                      text={
                        <div className="text-left space-y-1 p-1 font-mono text-[10px]">
                          <div>Image → 3D: 15 credits</div>
                          <div>Text → 3D: 15 credits</div>
                          <div>Refine model: 15 credits</div>
                        </div>
                      }
                    >
                      <InformationCircleIcon className="w-4 h-4 text-muted-foreground cursor-help" />
                    </Tooltip>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="p-1 rounded-full bg-purple-500/10 text-purple-500">
                      <QueueListIcon className="w-4 h-4" />
                    </div>
                    <span className="font-mono text-xs uppercase tracking-wide">{plan.priority}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="p-1 rounded-full bg-green-500/10 text-green-500">
                      <CheckIcon className="w-4 h-4" />
                    </div>
                    <span className="font-mono text-xs uppercase tracking-wide">Commercial license</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="p-1 rounded-full bg-green-500/10 text-green-500">
                      <CheckIcon className="w-4 h-4" />
                    </div>
                    <span className="font-mono text-xs uppercase tracking-wide">Private generation</span>
                  </div>
                </div>

                <button
                  onClick={() => handleSubscribe(lookupKey)}
                  disabled={loading}
                  className={`w-full py-4 rounded-xl font-bold text-xs uppercase tracking-widest transition-all ${plan.popular
                      ? 'bg-foreground text-background hover:bg-foreground/90 shadow-lg shadow-purple-500/20'
                      : 'bg-background/50 hover:bg-foreground/10 border border-border text-foreground'
                    }`}
                >
                  {loading ? 'Processing...' : 'Get Access'}
                </button>
              </div>
            );
          })}
        </div>
        <p className="text-center text-xs text-muted-foreground mt-8 uppercase tracking-widest">
          Secure payment via Stripe • Cancel anytime
        </p>
      </div>

      {/* FAQ Section - NEW */}
      <FAQ />
    </div>
  );
}
